import {
  JobStatus,
  PartImageEvidenceTier,
  PartImageIdentification,
  PartImageIdentificationOutcome,
} from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { Thumbnail } from '@/components/Thumbnail';
import { useCreateReportFromPartImageCandidate } from '@/hooks/api/part-finder-image-search/useCreateReportFromPartImageCandidate';
import { useGetPartImageIdentification } from '@/hooks/api/part-finder-image-search/useGetPartImageIdentification';
import { usePartImageSegments } from '@/hooks/api/part-finder-image-search/usePartImageSegments';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import {
  Alert,
  Box,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IdentificationProgress, identificationStep } from './IdentificationProgress';
import { itemsFoundLabel, MultipleItemsResult, ResultSubview } from './MultipleItemsResult';
import { NarrowByMeasurements } from './NarrowByMeasurements';
import { PartImageIdentificationResult } from './PartImageIdentificationResult';

const SUBMITTED_THUMB_SIZE = 48;
const PANES_SCROLL_ON_THEIR_OWN = { display: { md: 'flex' }, flexDirection: 'column', overflowY: { md: 'hidden' } };
const DIALOG_PAPER = { borderRadius: '12px', height: { md: 'min(880px, calc(100% - 64px))' } };

const sweep = keyframes`
  from { transform: translateY(-100%); }
  to { transform: translateY(100%); }
`;

const ScanSweep = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  background: `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.info.light, 0.5)} 50%, transparent 100%)`,
  animation: `${sweep} 1.6s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
}));

const isFinished = (identification?: PartImageIdentification): identification is PartImageIdentification =>
  identification?.status === JobStatus.COMPLETED || identification?.status === JobStatus.FAILED;

const NOT_NARROWABLE = new Set<PartImageIdentificationOutcome | undefined>([
  PartImageIdentificationOutcome.ReportCreated,
  PartImageIdentificationOutcome.NoPart,
  PartImageIdentificationOutcome.MultipleItems,
]);

export const canNarrow = (identification: PartImageIdentification): boolean =>
  !NOT_NARROWABLE.has(identification.outcome) &&
  identification.candidates.every(candidate => candidate.evidenceTier === PartImageEvidenceTier.Inferred);

const SubmittedImages = ({ images, scanning }: { images: File[]; scanning: boolean }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} data-testid='part-image-submitted'>
    {images.map((image, index) => (
      <Box
        key={`${image.name}-${index}`}
        sx={{
          position: 'relative',
          width: SUBMITTED_THUMB_SIZE,
          height: SUBMITTED_THUMB_SIZE,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Thumbnail file={image} size={SUBMITTED_THUMB_SIZE} noLink />
        {scanning && <ScanSweep data-testid='part-image-scanning' />}
      </Box>
    ))}
  </Box>
);

export interface ImagePartFinderModalProperties {
  images: File[];
  autoSplit: boolean;
  identificationId?: string;
  isSubmitting: boolean;
  submitError?: Error;
  onClose: () => void;
}

export const ImagePartFinderModal = ({
  images,
  autoSplit,
  identificationId,
  isSubmitting,
  submitError,
  onClose,
}: ImagePartFinderModalProperties) => {
  const createReport = useCreateReportFromPartImageCandidate();
  const { data: identification, error: pollError } = useGetPartImageIdentification(identificationId);
  const { segments, isSegmenting, canSegment } = usePartImageSegments(images, autoSplit);
  const [subview, setSubview] = useState<ResultSubview>();
  const { showToast } = useToast();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const openReport = (reportId: string) => {
    navigate(getPath(`part-finder-reports/${reportId}`));
    onClose();
  };

  const openBatch = (batchId: string) => {
    showToast({ message: 'Part finder batch created successfully', type: 'success' });
    navigate(getPath(`part-finder/batches/${batchId}`));
    onClose();
  };

  const handleCreateReport = (niin: string) => {
    if (!identificationId) return;
    createReport.mutate(
      { identificationId, niin },
      {
        onSuccess: report => {
          showToast({ message: 'Part finder report created successfully', type: 'success' });
          openReport(report.id);
        },
      }
    );
  };

  const isIdentifying = isSubmitting || (identificationId !== undefined && !pollError && !isFinished(identification));
  const isComplete = identification?.status === JobStatus.COMPLETED;
  const narrowable = isComplete && canNarrow(identification);
  const multipleItems = isComplete && identification.outcome === PartImageIdentificationOutcome.MultipleItems;
  const apiError = getApiError(submitError ?? pollError ?? createReport.error ?? undefined, 'Image search failed');

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      slotProps={{ paper: { sx: DIALOG_PAPER } }}
      data-testid='image-part-finder-modal'
    >
      <DialogTitle sx={{ pb: 1, pr: 6, display: 'flex', alignItems: 'center', gap: 1 }}>
        {subview && (
          <IconButton
            size='small'
            aria-label='Back to items'
            onClick={subview.onBack}
            sx={{ ml: -1 }}
            data-testid='image-part-finder-back'
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        {subview?.title ?? 'Find from images'}
      </DialogTitle>
      <IconButton
        aria-label='Close'
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8 }}
        data-testid='image-part-finder-close'
      >
        <CloseIcon />
      </IconButton>
      <Collapse in={isIdentifying}>
        <LinearProgress color='info' sx={{ mx: 3, mb: 1, height: 4, borderRadius: 2, bgcolor: 'action.hover' }} />
      </Collapse>
      <DialogContent sx={{ pt: 1, ...PANES_SCROLL_ON_THEIR_OWN }}>
        <Stack gap='16px' sx={{ flex: { md: 1 }, minHeight: 0 }}>
          {!subview && (
            <Stack direction='row' columnGap='24px' rowGap='12px' alignItems='center' flexWrap='wrap'>
              <SubmittedImages images={images} scanning={isIdentifying} />
              {multipleItems && (
                <Typography variant='subtitle1' data-testid='part-image-items-heading'>
                  {itemsFoundLabel(identification)}
                </Typography>
              )}
              {isIdentifying && <IdentificationProgress step={identificationStep(identificationId, identification)} />}
            </Stack>
          )}
          <ApiErrorAlert error={apiError} />
          {identification?.status === JobStatus.FAILED && (
            <Alert severity='error'>{identification.failureReason ?? 'Identification failed. Try again.'}</Alert>
          )}
          {multipleItems && (
            <MultipleItemsResult
              key={identification.items.map(item => item.photoNumbers.join('-')).join('|')}
              identification={identification}
              images={images}
              segments={segments}
              isSegmenting={isSegmenting}
              canEditCrop={canSegment}
              onSubview={setSubview}
              onBatchCreated={openBatch}
            />
          )}
          {(isIdentifying || isComplete) && !multipleItems && (
            <PartImageIdentificationResult
              identification={identification}
              isCreating={createReport.isPending}
              onCreateReport={handleCreateReport}
              onOpenReport={openReport}
              narrowing={narrowable && <NarrowByMeasurements identificationId={identification.id} />}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
